import { defineStore } from 'pinia';
import {
    ref, computed, shallowRef, watch,
} from 'vue';
import {
    addVariablePrefix,
    applyStaticCssVariables,
    applyThemeCssVariables,
    hasAppliedStaticCssVariables,
} from '@/utils/buildCSSVariables.ts';
import cssVariablesJson from '@/assets/cssVariables.json';
import { updateChartsThemeStyle } from '@/utils/chartGlobalSettings.ts';
import { useMediaQuery } from '@vueuse/core';
import { SCREEN_SIZES } from '@/constants/ScreenSizes.ts';
import {
    DEFAULT_COLOUR_PALETTE_ID,
    SYSTEM_PREFERENCE_COLOUR_PALETTE_ID,
    getActiveColourPaletteMode,
    getFallbackColourPaletteId,
    getStoredColourPaletteId,
    getThemeVariablesForPalette,
    isTimeSensitiveAutomaticColourPaletteId,
    resolveColourPaletteId,
    setStoredColourPaletteId,
} from '@/constants/ColourPalettes.ts';
import type { ColourPaletteId, ColourPaletteMode } from '@/constants/ColourPalettes.ts';
import type { SiteSettingsOpenOptions } from '@/types/siteSettings/SiteSettingsTypes.ts';

const AUTOMATIC_PALETTE_REFRESH_INTERVAL_MS = 15 * 60 * 1000;

interface IPendingPaletteApplication {
    resolvedColourPaletteId: ColourPaletteId;
    isDarkModeFallback: boolean;
}

const useUIStore = defineStore('uiStore', () => {
    // #region css variables and theme
    const cssVariables = shallowRef<Record<string, string>>({});

    const theme = ref<ColourPaletteMode>('dark');
    const colourPaletteId = ref<ColourPaletteId>(DEFAULT_COLOUR_PALETTE_ID);
    const colourPaletteRefreshDate = ref<Date>(new Date());
    const colourPaletteRevision = ref<number>(0);
    const paletteRemountGeneration = ref<number>(0);
    const dashboardPaletteHostCount = ref<number>(0);
    const pendingPaletteApplication = ref<IPendingPaletteApplication | null>(null);
    let paletteUnmountAckCount = 0;
    const videoGuideModalOpen = ref<boolean>(false);
    const showPageLoader = ref<boolean>(false);
    const showNavSidebar = ref<boolean>(false);
    const mostRecentEnactRoute = ref<string | null>(null);
    const systemPrefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
    let automaticPaletteRefreshTimer: number | null = null;
    const siteSettingsModalVisible = ref<boolean>(false);
    const siteSettingsModalRequest = ref<SiteSettingsOpenOptions | null>(null);

    function createColourArray (): string[] {
        const colours = [];

        const primaryColours = Object.entries(cssVariablesJson.lcpColors)
            .filter(([key]) => key !== 'lcpMidnight')
            .map(([, value]) => value);

        colours.push(...primaryColours);
        colours.push(...Object.values(cssVariablesJson.lcpColorsSecondary));
        colours.push(...Object.values(cssVariablesJson.additionalChartColours));

        return colours;
    }

    const CHART_COLOURS: string[] = createColourArray();

    const activeThemeVariables = computed(() => getThemeVariablesForPalette(
        colourPaletteId.value,
        theme.value === 'dark',
        colourPaletteRefreshDate.value,
        systemPrefersDarkMode.value,
    ));
    const isDarkTheme = computed(() => theme.value === 'dark');

    const cssThemeVariables = computed(() => ({

        tableRow: activeThemeVariables.value.surface.neutral200,

        primaryFont: activeThemeVariables.value.surface.neutral1000,

        neutral0: activeThemeVariables.value.surface.neutral0,
        neutral100: activeThemeVariables.value.surface.neutral100,
        neutral200: activeThemeVariables.value.surface.neutral200,
        neutral300: activeThemeVariables.value.surface.neutral300,
        neutral400: activeThemeVariables.value.surface.neutral400,
        neutral500: activeThemeVariables.value.surface.neutral500,
        neutral600: activeThemeVariables.value.surface.neutral600,
        neutral700: activeThemeVariables.value.surface.neutral700,
        neutral800: activeThemeVariables.value.surface.neutral800,
        neutral900: activeThemeVariables.value.surface.neutral900,
        neutral1000: activeThemeVariables.value.surface.neutral1000,
        neutral0Alpha: activeThemeVariables.value.surface.neutral0Alpha,

        actionColour: activeThemeVariables.value.colours.primary.colour,
        actionColourHover: activeThemeVariables.value.colours.primary.hover,
        actionColourText: activeThemeVariables.value.colours.primary.text,
        accentColour: activeThemeVariables.value.colours.secondary.colour,
        accentColourHover: activeThemeVariables.value.colours.secondary.hover,
        accentColourText: activeThemeVariables.value.colours.secondary.text,
        successGreen: activeThemeVariables.value.colours.success.colour,
        successGreenText: activeThemeVariables.value.colours.success.text,
        warningYellow: activeThemeVariables.value.colours.warning.colour,
        warningYellowText: activeThemeVariables.value.colours.warning.text,
        dangerRed: activeThemeVariables.value.colours.danger.colour,
        dangerRedText: activeThemeVariables.value.colours.danger.text,

        shadow1: activeThemeVariables.value.shadow.shadow1,
        shadow2: activeThemeVariables.value.shadow.shadow2,
        shadow3: activeThemeVariables.value.shadow.shadow3,
        shadow4: activeThemeVariables.value.shadow.shadow4,
        shadow5: activeThemeVariables.value.shadow.shadow5,

        chartBackground: activeThemeVariables.value.chart.chartBackground,
        chartGridLineColour: activeThemeVariables.value.chart.chartGridLineColour,
        chartLineColour: activeThemeVariables.value.chart.chartLineColour,
        chartTextColour: activeThemeVariables.value.chart.chartTextColour,
    }));

    function getStaticCssVariables (): Record<string, string> {
        return {
            ...cssVariablesJson.lcpColors,
            ...cssVariablesJson.lcpColorsSecondary,
            ...cssVariablesJson.lcpColorsModifiers,
            ...cssVariablesJson.fonts,
            ...cssVariablesJson.border,
            ...cssVariablesJson.fontWeights,
            ...cssVariablesJson.sidebar,
            ...cssVariablesJson.headerbar,
            ...cssVariablesJson.spacing,
            ...cssVariablesJson.widths,
            ...cssVariablesJson.buttons,
            ...cssVariablesJson.charts,
        };
    }

    function setCssVariables (): void {
        const staticCssVariables = getStaticCssVariables();
        const themeCssVariables = cssThemeVariables.value;
        const newCssVariables = {
            ...staticCssVariables,
            ...themeCssVariables,
        };

        if (!hasAppliedStaticCssVariables()) {
            applyStaticCssVariables({
                vars: addVariablePrefix(staticCssVariables),
            });
        }

        applyThemeCssVariables({
            vars: addVariablePrefix(themeCssVariables),
        });

        cssVariables.value = newCssVariables;
    }

    function refreshActiveColourPalette (isDarkModeFallback = true): void {
        colourPaletteRefreshDate.value = new Date();
        theme.value = getActiveColourPaletteMode(
            colourPaletteId.value,
            isDarkModeFallback,
            systemPrefersDarkMode.value,
        );

        window.document.documentElement.classList.toggle('dark-mode', theme.value === 'dark');
        setCssVariables();
        updateChartsThemeStyle(activeThemeVariables.value);
        colourPaletteRevision.value += 1;
    }

    function commitColourPalette (
        resolvedColourPaletteId: ColourPaletteId,
        isDarkModeFallback = true,
    ): void {
        colourPaletteId.value = resolvedColourPaletteId;
        refreshActiveColourPalette(isDarkModeFallback);
        paletteRemountGeneration.value += 1;
        setStoredColourPaletteId(resolvedColourPaletteId);
        syncAutomaticPaletteRefreshTimer();
        pendingPaletteApplication.value = null;
        paletteUnmountAckCount = 0;
    }

    function requestColourPaletteUpdate (
        isDarkModeFallback: boolean,
        resolvedColourPaletteId: ColourPaletteId,
    ): void {
        if (dashboardPaletteHostCount.value > 0) {
            pendingPaletteApplication.value = {
                resolvedColourPaletteId,
                isDarkModeFallback,
            };
            paletteUnmountAckCount = 0;
            return;
        }

        commitColourPalette(resolvedColourPaletteId, isDarkModeFallback);
    }

    function registerDashboardPaletteHost (): void {
        dashboardPaletteHostCount.value += 1;
    }

    function unregisterDashboardPaletteHost (): void {
        dashboardPaletteHostCount.value = Math.max(0, dashboardPaletteHostCount.value - 1);
    }

    function notifyDashboardUnmountedForPalette (): void {
        const pending = pendingPaletteApplication.value;
        if (!pending) return;

        paletteUnmountAckCount += 1;
        if (paletteUnmountAckCount < dashboardPaletteHostCount.value) return;

        commitColourPalette(pending.resolvedColourPaletteId, pending.isDarkModeFallback);
    }

    function clearAutomaticPaletteRefreshTimer (): void {
        if (automaticPaletteRefreshTimer === null) return;
        window.clearInterval(automaticPaletteRefreshTimer);
        automaticPaletteRefreshTimer = null;
    }

    function syncAutomaticPaletteRefreshTimer (): void {
        if (!isTimeSensitiveAutomaticColourPaletteId(colourPaletteId.value)) {
            clearAutomaticPaletteRefreshTimer();
            return;
        }

        if (automaticPaletteRefreshTimer !== null) return;

        automaticPaletteRefreshTimer = window.setInterval(() => {
            if (!isTimeSensitiveAutomaticColourPaletteId(colourPaletteId.value)) {
                clearAutomaticPaletteRefreshTimer();
                return;
            }

            requestColourPaletteUpdate(false, colourPaletteId.value);
        }, AUTOMATIC_PALETTE_REFRESH_INTERVAL_MS);

        (automaticPaletteRefreshTimer as unknown as { unref?: () => void }).unref?.();
    }

    function applyColourPalette (newColourPaletteId: unknown, isDarkModeFallback = true): void {
        const resolvedColourPaletteId = resolveColourPaletteId(newColourPaletteId, isDarkModeFallback);
        requestColourPaletteUpdate(isDarkModeFallback, resolvedColourPaletteId);
    }

    function toggleTheme (isDarkMode: boolean): void {
        applyColourPalette(getFallbackColourPaletteId(isDarkMode), isDarkMode);
    }

    function hydrateColourPaletteFromStorage (): boolean {
        const storedColourPaletteId = getStoredColourPaletteId();
        if (!storedColourPaletteId) return false;

        applyColourPalette(storedColourPaletteId);
        return true;
    }

    watch(systemPrefersDarkMode, () => {
        if (colourPaletteId.value !== SYSTEM_PREFERENCE_COLOUR_PALETTE_ID) return;
        requestColourPaletteUpdate(theme.value === 'dark', colourPaletteId.value);
    });
    // #endregion

    // #region page loader
    interface ILoaderOptions {
        transparency?: boolean;
        teleportId?: string;
    }

    const defaultLoaderOptions = {
        transparency: false,
    };
    const loaderOptions = ref<ILoaderOptions>(defaultLoaderOptions);

    function setPageLoader (showLoader: boolean, newLoaderOptions?: ILoaderOptions): void {
        showPageLoader.value = showLoader;
        if (newLoaderOptions) {
            loaderOptions.value = newLoaderOptions;
        }
        if (!showLoader) {
            loaderOptions.value = { ...defaultLoaderOptions };
        }
    }
    // #endregion

    // #region page sizes and responsive
    const pageHeight = ref<number>(window.innerHeight);

    function setPageHeight (height: number): void {
        pageHeight.value = height;
    }

    const pageWidth = ref<number>(window.innerWidth);

    const isMobileView = useMediaQuery(`(max-width: ${SCREEN_SIZES.MOBILE_MAX_WIDTH}px)`);

    function setPageWidth (width: number): void {
        pageWidth.value = width;
    }

    function updateShowNavSidebar (show: boolean): void {
        showNavSidebar.value = show;
    }

    function openSiteSettings (options: SiteSettingsOpenOptions = {}): void {
        siteSettingsModalRequest.value = options;
        siteSettingsModalVisible.value = true;
    }

    function toggleSiteSettings (): void {
        if (siteSettingsModalVisible.value) {
            siteSettingsModalVisible.value = false;
            return;
        }

        openSiteSettings();
    }

    // #endregion

    return {
        CHART_COLOURS,
        colourPaletteId,
        colourPaletteRevision,
        paletteRemountGeneration,
        pendingPaletteApplication,
        dashboardPaletteHostCount,
        cssVariables,
        cssThemeVariables,
        isDarkTheme,
        isMobileView,
        pageHeight,
        pageWidth,
        showPageLoader,
        theme,
        videoGuideModalOpen,
        applyColourPalette,
        registerDashboardPaletteHost,
        unregisterDashboardPaletteHost,
        notifyDashboardUnmountedForPalette,
        hydrateColourPaletteFromStorage,
        setCssVariables,
        setPageHeight,
        setPageLoader,
        setPageWidth,
        toggleTheme,
        loaderOptions,
        showNavSidebar,
        updateShowNavSidebar,
        mostRecentEnactRoute,
        siteSettingsModalVisible,
        siteSettingsModalRequest,
        openSiteSettings,
        toggleSiteSettings,
    };
});

export {
    useUIStore,
};
